import type { ListLabelsResponse } from "@repo/marketplace-admin"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { createFileRoute } from "@tanstack/react-router"
import type { ColDef, ICellRendererParams } from "ag-grid-community"
import { Plus } from "lucide-react"
import * as React from "react"
import { toast } from "sonner"
import { PageHeader } from "@/components/layout"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Skeleton } from "@/components/ui/skeleton"
import { StatusDot } from "@/components/ui/status"
import { ScannerGrid } from "@/features/card-scanner/ScannerGrid"
import { QueryError, useScannerGate } from "@/features/card-scanner/shared"
import { useMarketplaceOrpc } from "@/integrations/orpc/marketplace-react"

export const Route = createFileRoute("/card-scanner/labels")({
	component: LabelsPage,
})

type LabelRow = ListLabelsResponse["labels"][number]

function LabelsPage() {
	const allowed = useScannerGate()
	const { orpc, isAuthReady } = useMarketplaceOrpc()
	const [addOpen, setAddOpen] = React.useState(false)

	const { data, isLoading, isError, error } = useQuery({
		...orpc.cardScanner.listLabels.queryOptions(),
		enabled: allowed && isAuthReady,
	})

	const columnDefs = React.useMemo<ColDef<LabelRow>[]>(
		() => [
			{
				headerName: "#",
				field: "labelIndex",
				sort: "asc",
				minWidth: 70,
				maxWidth: 90,
				type: "rightAligned",
				cellClass: "font-mono tabular-nums",
			},
			{
				headerName: "Label",
				field: "label",
				minWidth: 150,
				cellClass: "font-medium",
			},
			{
				headerName: "Games",
				field: "gameSlugs",
				minWidth: 260,
				sortable: false,
				cellRenderer: (p: ICellRendererParams<LabelRow>) =>
					p.data ? (
						<span className="inline-flex flex-wrap gap-1">
							{p.data.gameSlugs.map((slug) => (
								<Badge
									key={slug}
									variant="neutral"
									className="font-mono font-normal"
								>
									{slug}
								</Badge>
							))}
						</span>
					) : null,
			},
			{
				headerName: "Set symbols",
				field: "hasSetSymbols",
				minWidth: 110,
				valueFormatter: (p) => (p.value ? "yes" : "—"),
				cellClassRules: { "text-meta": (p) => !p.value },
			},
			{
				headerName: "Enabled",
				field: "enabled",
				minWidth: 110,
				cellRenderer: (p: ICellRendererParams<LabelRow>) =>
					p.data ? (
						<StatusDot tone={p.data.enabled ? "ok" : "neutral"}>
							{p.data.enabled ? "enabled" : "disabled"}
						</StatusDot>
					) : null,
			},
		],
		[],
	)

	if (!allowed) return null

	return (
		<div className="flex flex-col gap-6 p-6">
			<PageHeader
				title="Classifier labels"
				count={data?.labels.length}
				description="Append-only registry; indices are baked into trained classifiers and never move or get reused"
				actions={
					<Button onClick={() => setAddOpen(true)}>
						<Plus className="h-4 w-4" />
						Add label
					</Button>
				}
			/>

			{isLoading ? (
				<Skeleton className="h-64 w-full" />
			) : isError ? (
				<QueryError what="classifier labels" error={error} />
			) : data?.labels.length === 0 ? (
				<p className="text-muted-foreground">
					No labels yet. Seed them with the backfill script once SWM confirms
					the shipped classifier's class order, or append them manually.
				</p>
			) : (
				<ScannerGrid<LabelRow>
					columnDefs={columnDefs}
					rowData={data?.labels}
					getRowId={(row) => String(row.labelIndex)}
				/>
			)}

			{addOpen && <AddLabelDialog onClose={() => setAddOpen(false)} />}
		</div>
	)
}

function AddLabelDialog({ onClose }: { onClose: () => void }) {
	const { orpc, client } = useMarketplaceOrpc()
	const queryClient = useQueryClient()
	const [label, setLabel] = React.useState("")
	const [gameSlugsText, setGameSlugsText] = React.useState("")
	const [hasSetSymbols, setHasSetSymbols] = React.useState(false)

	const mutation = useMutation({
		mutationFn: () =>
			client.cardScanner.addLabel({
				label: label.trim(),
				gameSlugs: gameSlugsText
					.split(",")
					.map((slug) => slug.trim())
					.filter(Boolean),
				hasSetSymbols,
			}),
		onSuccess: ({ label: created }) => {
			toast.success(
				`Label ${created.label} added at index ${created.labelIndex}`,
			)
			queryClient.invalidateQueries({
				queryKey: orpc.cardScanner.listLabels.key(),
			})
			onClose()
		},
		onError: (error) => toast.error(error.message),
	})

	return (
		<Dialog open onOpenChange={(open) => !open && onClose()}>
			<DialogContent>
				<DialogHeader>
					<DialogTitle>Add classifier label</DialogTitle>
					<DialogDescription>
						Appended at the next index. It cannot be reordered or removed later,
						only disabled.
					</DialogDescription>
				</DialogHeader>
				<div className="space-y-4">
					<div className="space-y-2">
						<Label>Label</Label>
						<Input
							value={label}
							onChange={(event) => setLabel(event.target.value)}
							placeholder="e.g. riftbound or DBS"
						/>
					</div>
					<div className="space-y-2">
						<Label>Game slugs (comma-separated)</Label>
						<Input
							value={gameSlugsText}
							onChange={(event) => setGameSlugsText(event.target.value)}
							placeholder="e.g. dbs-fusion, dbs-masters"
						/>
					</div>
					<div className="flex items-center gap-2">
						<Checkbox
							id="has-set-symbols"
							checked={hasSetSymbols}
							onCheckedChange={(checked) => setHasSetSymbols(checked === true)}
						/>
						<Label htmlFor="has-set-symbols">Has set symbols</Label>
					</div>
				</div>
				<DialogFooter>
					<Button variant="outline" onClick={onClose}>
						Cancel
					</Button>
					<Button
						onClick={() => mutation.mutate()}
						disabled={
							mutation.isPending || !label.trim() || !gameSlugsText.trim()
						}
					>
						Add
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	)
}
